export interface TalentDiagnosticsPublicOption {
  key: string;
  text: string;
}

export interface TalentDiagnosticsProgressMessage {
  heading: string;
  body: string;
}

export const getTalentDiagnosticsSectionProgress = ({ activeSectionIndex, sectionCount, isSubmissionComplete = false }: { activeSectionIndex: number; sectionCount: number; isSubmissionComplete?: boolean }): number => {
  if (sectionCount <= 0) {
    return 0;
  }

  if (isSubmissionComplete) {
    return 100;
  }

  const rawProgress = Math.round(((activeSectionIndex + 1) / sectionCount) * 100);
  return Math.min(rawProgress, 99);
};

export const getTalentDiagnosticsQuestionPosition = ({ activeSectionIndex, sections, questions }: { activeSectionIndex: number; sections: Array<{ id?: string }>; questions: Array<{ sectionId?: string }> }): number => {
  if (questions.length === 0) {
    return 0;
  }

  const questionsBeforeCurrentSection = sections
    .slice(0, activeSectionIndex)
    .reduce((sum, section) => {
      const count = questions.filter((question) => question.sectionId === section.id).length;
      return sum + count;
    }, 0);

  const currentSectionQuestionCount = questions.filter((question) => question.sectionId === sections[activeSectionIndex]?.id).length;

  if (currentSectionQuestionCount <= 0) {
    return questionsBeforeCurrentSection + 1;
  }

  return questionsBeforeCurrentSection + 1;
};

export interface TalentDiagnosticsNormalizedDefinitionPayload {
  sections: Array<{
    id?: string;
    name?: string;
    description?: string;
    questions: Array<{
      id?: string;
      key?: string;
      text?: string;
      description?: string;
      type?: string;
      optionsJson?: string | null;
      sectionId?: string;
    }>;
  }>;
  benchmarks: Array<{
    id?: string;
    name?: string;
    description?: string;
    recommendationText?: string;
  }>;
  recommendations: Array<{
    id?: string;
    title?: string;
    bodyText?: string;
  }>;
}

export const parseTalentDiagnosticsOptionJson = (value?: string | null): TalentDiagnosticsPublicOption[] => {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
      .map((item) => ({
        key: String(item.key ?? ''),
        text: String(item.text ?? ''),
      }))
      .filter((item) => item.key || item.text);
  } catch {
    return [];
  }
};

export const normalizeTalentDiagnosticsDefinitionPayload = (payload: {
  sections?: Array<Record<string, unknown>>;
  benchmarks?: Array<Record<string, unknown>>;
  recommendations?: Array<Record<string, unknown>>;
}): TalentDiagnosticsNormalizedDefinitionPayload => {
  const sections = Array.isArray(payload.sections) ? payload.sections : [];
  const benchmarks = Array.isArray(payload.benchmarks) ? payload.benchmarks : [];
  const recommendations = Array.isArray(payload.recommendations) ? payload.recommendations : [];

  return {
    sections: sections.map((section) => {
      const questionList = Array.isArray((section as Record<string, unknown>).questions)
        ? ((section as Record<string, unknown>).questions as Array<Record<string, unknown>>)
        : [];

      return {
        id: String((section as Record<string, unknown>).id ?? ''),
        name: String((section as Record<string, unknown>).name ?? ''),
        description: String((section as Record<string, unknown>).description ?? ''),
        questions: questionList.map((question) => ({
          id: String(question.id ?? ''),
          key: String(question.key ?? ''),
          text: String(question.text ?? ''),
          description: String(question.description ?? ''),
          type: String(question.type ?? ''),
          optionsJson: question.optionsJson ? String(question.optionsJson) : null,
          sectionId: String(question.sectionId ?? (section as Record<string, unknown>).id ?? ''),
        })),
      };
    }),
    benchmarks: benchmarks.map((benchmark) => ({
      id: String(benchmark.id ?? ''),
      name: String(benchmark.name ?? ''),
      description: String(benchmark.description ?? ''),
      recommendationText: parseRecommendationText(benchmark.recommendationJson),
    })),
    recommendations: recommendations.map((recommendation) => ({
      id: String(recommendation.id ?? ''),
      title: String(recommendation.title ?? ''),
      bodyText: parseRecommendationText(recommendation.bodyJson),
    })),
  };
};

export const parseRecommendationText = (value?: unknown): string | undefined => {
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'string') {
      return parsed;
    }

    if (parsed && typeof parsed === 'object') {
      if (typeof (parsed as Record<string, unknown>).body === 'string') {
        return (parsed as Record<string, unknown>).body as string;
      }

      if (typeof (parsed as Record<string, unknown>).content === 'string') {
        return (parsed as Record<string, unknown>).content as string;
      }

      if (typeof (parsed as Record<string, unknown>).text === 'string') {
        return (parsed as Record<string, unknown>).text as string;
      }

      if (typeof (parsed as Record<string, unknown>).recommendation === 'string') {
        return (parsed as Record<string, unknown>).recommendation as string;
      }

      if (typeof (parsed as Record<string, unknown>).overview === 'string') {
        return (parsed as Record<string, unknown>).overview as string;
      }

      if (typeof (parsed as Record<string, unknown>).summary === 'string') {
        return (parsed as Record<string, unknown>).summary as string;
      }
    }
  } catch {
    return value;
  }

  return value;
}

export const normalizeTalentDiagnosticsAnalysisResponse = (
  data: Record<string, unknown>
): {
  requiresLeadCapture?: boolean;
  leadCaptureId?: string | null;
  report?: unknown;
  benchmark?: {
    name?: string;
    description?: string;
    recommendationContent?: string;
  };
  sectionResults?: Array<{
    name?: string;
    score?: number;
    weighting?: number;
    isSkipped?: boolean;
  }>;
  strengths?: string[];
  improvementOpportunities?: string[];
  recommendations?: Array<{
    title?: string;
    overview?: string;
    recommendedActions?: string[];
    expectedOutcome?: string;
  }>;
  scoringSnapshotJson?: string;
  summaryReport?: string[];
  overallPercentage?: number;
  visualIndicator?: 'excellent' | 'neutral' | 'poor' | 'critical';
  reportPublicToken?: string;
  reportUrl?: string;
  result?: Record<string, unknown>;
} => {
  const result =
    (data.result as Record<string, unknown> | undefined) ?? {};

  const sectionScores = parseJsonArray(result.sectionScoresJson);
  const recommendations = parseJsonArray(result.recommendationsJson);

  const scoringSnapshot = parseJsonObject(
    result.scoringSnapshotJson
  );

  const matchedBenchmark = parseJsonObject(
    scoringSnapshot?.MatchedBenchmark
  );

  const benchmarkRecommendationJson =
    typeof matchedBenchmark?.RecommendationJson === 'string'
      ? matchedBenchmark.RecommendationJson
      : undefined;

  const benchmarkRecommendationData =
    parseJsonObject(benchmarkRecommendationJson);

  const benchmarkSummary =
    typeof benchmarkRecommendationData?.summary === 'string'
      ? (benchmarkRecommendationData.summary as string)
      : typeof matchedBenchmark?.Summary === 'string'
      ? (matchedBenchmark.Summary as string)
      : undefined;

  const benchmarkPriority =
    typeof benchmarkRecommendationData?.priority === 'string'
      ? (benchmarkRecommendationData.priority as string)
      : typeof matchedBenchmark?.Priority === 'string'
      ? (matchedBenchmark.Priority as string)
      : undefined;

  const benchmarkFocusAreas = parseStringArray(
    benchmarkRecommendationData?.focusAreas
  );

  const sectionResults = sectionScores.map((item) => ({
    name:
        typeof item?.Name === 'string'
        ? item.Name
        : undefined,

    score:
        typeof item?.Score === 'number'
        ? item.Score
        : undefined,

    weighting:
        typeof item?.Weighting === 'number'
        ? item.Weighting
        : undefined,

    isSkipped:
        typeof item?.IsSkipped === 'boolean'
        ? item.IsSkipped
        : false,

    answeredQuestionCount:
        typeof item?.AnsweredQuestionCount === 'number'
        ? item.AnsweredQuestionCount
        : 0,

    skippedQuestionCount:
        typeof item?.SkippedQuestionCount === 'number'
        ? item.SkippedQuestionCount
        : 0,
  }));

  const recommendationItems = recommendations.map((item) => {
    const bodyJson =
      typeof item?.BodyJson === 'string'
        ? item.BodyJson
        : undefined;

    const structuredBody = parseJsonObject(bodyJson);

    return {
      title:
        typeof item?.Title === 'string'
          ? item.Title
          : 'Recommendation',

      overview:
        typeof structuredBody?.overview === 'string'
          ? (structuredBody.overview as string)
          : undefined,

      recommendedActions: Array.isArray(
        structuredBody?.recommendedActions
      )
        ? structuredBody.recommendedActions.filter(
            (action): action is string =>
              typeof action === 'string'
          )
        : [],

      expectedOutcome:
        typeof structuredBody?.expectedOutcome === 'string'
          ? (structuredBody.expectedOutcome as string)
          : undefined,
    };
  });

  const strengths = benchmarkFocusAreas;

  const summaryReport = [
    benchmarkSummary
      ? `Benchmark summary: ${benchmarkSummary}`
      : undefined,

    benchmarkPriority
      ? `Priority: ${benchmarkPriority}`
      : undefined,

    recommendationItems.length > 0
      ? `Recommended focus: ${recommendationItems
          .map((item) => item.title)
          .join(' • ')}`
      : undefined,
  ].filter(Boolean) as string[];

  const overallScore =
    typeof result.overallScore === 'number'
      ? result.overallScore
      : typeof scoringSnapshot?.DisplayOverallScore ===
        'number'
      ? scoringSnapshot.DisplayOverallScore
      : 0;

  return {
    requiresLeadCapture:
      typeof data.requiresLeadCapture === 'boolean'
        ? data.requiresLeadCapture
        : false,

    leadCaptureId:
      typeof data.leadCaptureId === 'string' ||
      data.leadCaptureId === null
        ? data.leadCaptureId
        : undefined,

    report: data.report,

    benchmark: {
      name:
        typeof matchedBenchmark?.Name === 'string'
          ? matchedBenchmark.Name
          : undefined,

      description: benchmarkSummary,

      recommendationContent:
        benchmarkFocusAreas.join(', '),
    },

    sectionResults,

    strengths,

    improvementOpportunities: [],

    recommendations: recommendationItems,

    scoringSnapshotJson:
      typeof result.scoringSnapshotJson === 'string'
        ? result.scoringSnapshotJson
        : undefined,

    summaryReport: Array.isArray(data.summaryReport)
      ? (data.summaryReport as string[])
      : summaryReport,

    overallPercentage: Number(
      overallScore.toFixed(2)
    ),

    visualIndicator:
      getVisualIndicatorFromScore(overallScore),

    reportPublicToken:
      typeof data.reportPublicToken === 'string'
        ? data.reportPublicToken
        : undefined,

    reportUrl:
      typeof data.reportUrl === 'string'
        ? data.reportUrl
        : undefined,

    result,
  };
};

const parseJsonArray = (value?: unknown): Array<Record<string, unknown>> => {
  if (typeof value !== 'string' || !value.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object') : [];
  } catch {
    return [];
  }
};

const parseJsonObject = (value?: unknown): Record<string, unknown> | undefined => {
  if (typeof value === 'string') {
    if (!value.trim()) {
      return undefined;
    }

    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : undefined;
    } catch {
      return undefined;
    }
  }

  if (value && typeof value === 'object') {
    return value as Record<string, unknown>;
  }

  return undefined;
};

const parseStringArray = (value?: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && Boolean(item));
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed.filter((item): item is string => typeof item === 'string' && Boolean(item))
        : [];
    } catch {
      return [];
    }
  }

  return [];
};

const getVisualIndicatorFromScore = (score: number): 'excellent' | 'neutral' | 'poor' | 'critical' => {
  if (score >= 80) {
    return 'excellent';
  }

  if (score >= 60) {
    return 'neutral';
  }

  if (score >= 40) {
    return 'poor';
  }

  return 'critical';
};

export const getTalentDiagnosticsProgressMessage = (completionPercentage: number): TalentDiagnosticsProgressMessage => {
  if (completionPercentage <= 25) {
    return {
      heading: "Let's get started.",
      body: "Your responses will help build an accurate picture of your Talent Acquisition performance.",
    };
  }

  if (completionPercentage <= 50) {
    return {
      heading: 'Great progress.',
      body: 'You\'re building a strong foundation for your Talent Acquisition assessment.',
    };
  }

  if (completionPercentage <= 75) {
    return {
      heading: 'You\'re doing well.',
      body: 'The halfway point is already behind you.',
    };
  }

  if (completionPercentage <= 90) {
    return {
      heading: 'You\'re almost there.',
      body: 'Just a little bit more and your results will be ready.',
    };
  }

  if (completionPercentage < 100) {
    return {
      heading: 'Final questions.',
      body: 'Your Talent Acquisition diagnostic report is nearly complete.',
    };
  }

  return {
    heading: 'Excellent work.',
    body: 'Your assessment is complete and your results are being prepared.',
  };
};
